// Google Analytics 4 Enhanced Tracking
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export class GoogleAnalyticsService {
  private static isInitialized = false;

  static initialize() {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function' && !this.isInitialized) {
      this.isInitialized = true;
      console.log('Google Analytics 4 initialized');
    }
  }

  // Track page views with additional context
  static trackPageView(page: string, title?: string) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'G-RN56BE1BL2', {
        page_path: page,
        page_title: title || document.title
      });
    }
  }

  // Track capability page views
  static trackCapabilityView(capabilityName: string, capabilitySlug: string) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'capability_view', {
        event_category: 'Capabilities',
        event_label: capabilityName,
        capability_slug: capabilitySlug,
        value: 1
      });
    }
  }

  // Track service interest
  static trackServiceInterest(serviceName: string, source: string) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'service_interest', {
        event_category: 'Services',
        event_label: serviceName,
        source: source,
        value: 1
      });
    }
  }

  // Track contact form submissions
  static trackContactForm(service: string, hasCompany: boolean) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'contact_form_submit', {
        event_category: 'Lead Generation',
        event_label: service,
        has_company: hasCompany,
        value: 10 // Assign value to leads
      });
    }
  }

  // Track consultation bookings (high-value events)
  static trackConsultationBooking(service: string, hasCompany: boolean) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'consultation_booking', {
        event_category: 'High Value Conversion',
        event_label: service,
        has_company: hasCompany,
        value: 50 // Higher value for consultation bookings
      });
    }
  }

  // Track button clicks and interactions
  static trackButtonClick(buttonName: string, location: string) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'button_click', {
        event_category: 'Engagement',
        event_label: buttonName,
        location: location,
        value: 1
      });
    }
  }

  // Track file downloads (if any)
  static trackFileDownload(fileName: string, fileType: string) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'file_download', {
        event_category: 'Downloads',
        event_label: fileName,
        file_type: fileType,
        value: 5
      });
    }
  }

  // Track external link clicks
  static trackExternalLink(url: string, linkText: string) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'external_link_click', {
        event_category: 'External Links',
        event_label: linkText,
        external_url: url,
        value: 1
      });
    }
  }

  // Track time spent on capability pages
  static trackCapabilityEngagement(capabilitySlug: string, timeOnPage: number) {
    if (typeof window !== 'undefined' && window.gtag && timeOnPage > 30000) { // Only track if > 30 seconds
      window.gtag('event', 'capability_engagement', {
        event_category: 'Engagement',
        event_label: capabilitySlug,
        time_on_page: Math.round(timeOnPage / 1000), // Convert to seconds
        value: Math.min(Math.round(timeOnPage / 10000), 10) // Value based on engagement
      });
    }
  }

  // Track search queries (if search is added)
  static trackSearch(query: string, results: number) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'search', {
        search_term: query,
        results_count: results
      });
    }
  }

  // Track conversion events
  static trackConversion(conversionType: string, value: number) {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion', {
        event_category: 'Conversions',
        event_label: conversionType,
        value: value
      });
    }
  }
}

// Initialize on load
if (typeof window !== 'undefined') {
  GoogleAnalyticsService.initialize();
}